import React from "react";
import { euro } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BedDouble } from "lucide-react";
import { SelettoreGiorni } from "@/components/SelettoreGiorni";
import { SelettoreDipendenti } from "@/components/SelettoreDipendenti";
import { RimborsiOre } from "@/components/RimborsiOre";
import { TIPI_ORA, etichettaTipoOra } from "@/lib/tipiOra";


const TURNO_LABEL = { centrale: "Centrale", primo: "Primo turno", secondo: "Secondo turno", notturno: "Notturno", festivo: "Festivo" };
const hm = (t) => { const [h, m] = (t || "0:0").split(":"); return Number(h) * 60 + Number(m || 0); };
// Anteprima lato client del calcolo turno (il valore definitivo lo calcola il server con le stesse impostazioni)
export const anteprimaTurno = (v, imp) => {
  if (!v?.ora_inizio || !v?.ora_fine) return null;
  const ini = hm(v.ora_inizio), fin = hm(v.ora_fine);
  const durata = fin > ini ? fin - ini : fin + 1440 - ini;
  const t = imp?.turni || {};
  const d = new Date(`${(v.giorni?.[0] || v.data)}T00:00:00`);
  let turno = "centrale";
  if (d.getDay() === 0) turno = "festivo";
  else if (ini >= hm(t.notturno?.inizio || "22:00") || ini < hm(t.notturno?.fine || "06:00")) turno = "notturno";
  else if (ini < hm(t.centrale?.inizio || "08:00") && ini >= hm(t.primo?.inizio || "06:00")) turno = "primo";
  else if (ini >= hm(t.secondo?.inizio || "14:00")) turno = "secondo";
  // pausa sottratta solo nel turno Centrale (negli altri turni è già compresa)
  const pausa = turno === "centrale" ? Number(imp?.pause?.[v.luogo] ?? (v.luogo === "cantiere" ? 60 : v.luogo === "officina" ? 30 : 0)) : 0;
  const nette = Math.max(0, Math.round(((durata - pausa) / 60) * 100) / 100);
  const ord = Math.min(8, nette);
  return { turno, label: TURNO_LABEL[turno], nette, pausa, ord, str: Math.round((nette - ord) * 100) / 100 };
};

export const oggiIso = () => new Date().toISOString().slice(0, 10);
export const INTESTAZIONE_ORE_INIZIALE = () => ({
  data: oggiIso(), giorni: [], dipendenti_ids: [], luogo: "", tipo: "ordinario", note: "", ora_inizio: "", ora_fine: "",
  pasto_cantiere: false, cena_trasferta: false, trasferta: false,
});

// Se nel calendario sono selezionati giorni, si usano solo quelli; altrimenti la data del campo principale.
export const giorniEffettivi = (v) => (v.giorni && v.giorni.length > 0 ? [...new Set(v.giorni)].sort() : [v.data || oggiIso()]);
export const nottiDaGiorni = (n) => Math.max(n - 1, 0);

export function IntestazioneOre({ value: v, onChange, dipendenti, impostazioni, testid }) {
  const set = (patch) => onChange({ ...v, ...patch });
  const giorni = giorniEffettivi(v);
  const notti = nottiDaGiorni(giorni.length);
  const nD = v.dipendenti_ids.length;
  const ind = impostazioni?.indennita_notte || 0;
  const turnoInfo = anteprimaTurno(v, impostazioni);
  return (
    <div className="grid sm:grid-cols-3 gap-4" data-testid={`${testid}-intestazione`}>
      <div className="space-y-1.5">
        <Label>Data</Label>
        <Input type="date" value={v.data} onChange={(e) => set({ data: e.target.value })} className="bg-white" data-testid={`${testid}-data`} />
        <div className="pt-1">
          <SelettoreGiorni giorni={v.giorni} onChange={(g) => set({ giorni: g })} testid={`${testid}-giorni`} />
          <p className="text-xs text-slate-400 mt-1">
            {giorni.length > 1 || (v.giorni || []).length > 0 ? `Verranno create righe solo per i ${giorni.length} giorni selezionati nel calendario (la data qui sopra non viene inclusa se non è selezionata anch'essa).` : "Facoltativo: aggiungi altri giorni per registrare le stesse ore su ciascuno."}
          </p>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Dipendenti *</Label>
        <SelettoreDipendenti
          dipendenti={dipendenti}
          selezionati={v.dipendenti_ids}
          onChange={(ids) => set({ dipendenti_ids: ids })}
          etichetta={(d) => `${d.nome} ${d.cognome} — ${d.specializzazione || ""}`}
          testid={`${testid}-dipendente`}
        />
      </div>
      <div className="space-y-1.5">
        <Label>Luogo di lavoro *</Label>
        <Select value={v.luogo} onValueChange={(l) => set({ luogo: l, pasto_cantiere: l === "cantiere" })}>
          <SelectTrigger data-testid={`${testid}-luogo`}><SelectValue placeholder="Seleziona luogo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="officina">Officina</SelectItem>
            <SelectItem value="cantiere">Cantiere</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label>Tipo ora</Label>
        <Select value={v.tipo || "ordinario"} onValueChange={(t) => set({ tipo: t })}>
          <SelectTrigger data-testid={`${testid}-tipo-ora`}><SelectValue /></SelectTrigger>
          <SelectContent>{TIPI_ORA.map((t) => <SelectItem key={t.k} value={t.k} data-testid={`${testid}-tipo-ora-${t.k}`}>{etichettaTipoOra(t.k, impostazioni)}</SelectItem>)}</SelectContent>
        </Select>
        <p className="text-xs text-slate-400">Si applica alle ore del campo "Ore"; le ore straordinarie extra usano sempre la maggiorazione Straordinario.</p>
      </div>
      <div className="space-y-1.5">
        <Label>Ora inizio / Ora fine <span className="text-slate-400 font-normal">(opzionale)</span></Label>
        <div className="flex items-center gap-2">
          <Input type="time" value={v.ora_inizio || ""} onChange={(e) => set({ ora_inizio: e.target.value })} className="w-full" data-testid={`${testid}-ora-inizio`} />
          <span className="text-slate-400">→</span>
          <Input type="time" value={v.ora_fine || ""} onChange={(e) => set({ ora_fine: e.target.value })} className="w-full" data-testid={`${testid}-ora-fine`} />
        </div>
        <p className="text-xs text-slate-400" data-testid={`${testid}-turno-info`}>{turnoInfo ? `Turno ${turnoInfo.label} · ${turnoInfo.nette} h nette (${turnoInfo.turno === "centrale" ? `pausa ${turnoInfo.pausa} min ${v.luogo || "—"}` : "pausa già compresa"}): ${turnoInfo.ord} ordinarie${turnoInfo.str > 0 ? ` + ${turnoInfo.str} straordinarie` : ""}. Lascia vuote le ore sulle commesse per usarle.` : "Se compilato, turno e ore (meno la pausa del luogo) vengono calcolati automaticamente."}</p>
      </div>
      <div className="space-y-1.5">
        <Label>Note</Label>
        <Input value={v.note} onChange={(e) => set({ note: e.target.value })} placeholder="Facoltative, applicate a tutte le righe" data-testid={`${testid}-note`} />
      </div>
      <RimborsiOre
        pasto={v.pasto_cantiere} onPasto={(p) => set({ pasto_cantiere: p })}
        cena={v.cena_trasferta} onCena={(c) => set({ cena_trasferta: c })}
        impostazioni={impostazioni} testid={testid}
      />
      {v.cena_trasferta && !v.trasferta && (
        <p className={`text-xs -mt-2 sm:col-start-2 ${notti === 0 ? "text-red-600" : "text-sky-700"}`} data-testid={`${testid}-cena-info`}>
          {notti === 0 ? "Cena = giorni − 1: con un solo giorno non viene registrata nessuna cena. Aggiungi il giorno di rientro." : `${giorni.length} giorni → ${notti} cen${notti === 1 ? "a" : "e"} per dipendente.`}
        </p>
      )}
      <div className="space-y-1.5" data-testid={`${testid}-trasferta-box`}>
        <Label>Trasferta</Label>
        <label className={`flex items-center gap-2 rounded-lg border px-3 h-10 cursor-pointer text-sm select-none transition-colors ${v.trasferta ? "border-primary/40 bg-orange-50 text-slate-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          <Checkbox checked={v.trasferta} onCheckedChange={(c) => set({ trasferta: !!c, cena_trasferta: c ? true : v.cena_trasferta })} data-testid={`${testid}-trasferta`} />
          <BedDouble className="h-4 w-4" /> Notti fuori sede <span className="font-mono text-xs opacity-70">{euro(ind)}/notte</span>
        </label>
        {v.trasferta && (
          <p className={`text-xs ${notti === 0 ? "text-red-600" : "text-primary"}`} data-testid={`${testid}-trasferta-info`}>
            {notti === 0
              ? "Regola notti = giorni − 1: con un solo giorno non viene registrata nessuna notte. Aggiungi il giorno di rientro."
              : <>{giorni.length} giorni → <b>{notti} nott{notti === 1 ? "e" : "i"}</b> e {notti} cen{notti === 1 ? "a" : "e"} per dipendente{nD > 0 && <> · {nD} × {notti} = <b>{nD * notti}</b> notti · indennità {euro(nD * notti * ind)}</>}. L'ultimo giorno è il rientro; le notti finiscono nei costi commessa e in Ore Mensili (Indennità trasferta).</>}
          </p>
        )}
      </div>
    </div>
  );
}
